import CallRecordsResource from 'flow/api/callrecords';
import Actions from 'flow/actions/index';
import moment from 'moment';
import { State } from 'flow/types/State';
import { networkErrorDispatcher } from 'flow/actions/app-actions';
import {
    CallActionKey,
    CallRecordKey,
    ExtensionKey,
    QueueKey,
} from 'flow/types/EntityKeys';
import { selectContact } from 'flow/selectors/ExtensionSelectors';
import { selectQueueFavorites } from 'flow/selectors/favorite-selectors';
import { selectCallRecordFromKey } from 'flow/selectors/call-records-selectors';
import { selectMissedCallsWidgetIsMounted } from 'flow/selectors/app-selectors';
import { Callback } from 'flow/types/HomeClient';
import { AppDispatch } from 'flow/types/Redux';
import { CALL_RECORD_INCLUDES_LIST } from 'flow/utils/call-record-fetch-includes';

export const fetchQueueCallRecordsUnhandled =
    (
        favoritesOnlyFilter?: boolean,
        userIsMemberFilter?: boolean,
        before?: number,
        since?: number,
        overwriteExistingData?: boolean,
    ) =>
    (dispatch: AppDispatch) => {
        return new Promise((resolve, reject) => {
            CallRecordsResource.getQueueCallRecordsNotHandled(
                {
                    onResult: (response) => {
                        dispatch({
                            type: Actions.RESULT_QUEUE_CALL_RECORDS_UNHANDLED_WIDGET,
                            data: response,
                            overwriteExistingData,
                            include: CALL_RECORD_INCLUDES_LIST,
                        });
                        resolve(response);
                    },
                    onClientError: reject,
                    onError: (error) => {
                        networkErrorDispatcher(dispatch);
                        reject(error);
                    },
                } as Callback,
                favoritesOnlyFilter,
                userIsMemberFilter,
                before,
                since,
                CALL_RECORD_INCLUDES_LIST,
            );
        });
    };

export const refreshMissedQueueCallsWidgetData =
    () => (dispatch: AppDispatch, getState: () => State) => {
        if (!selectMissedCallsWidgetIsMounted(getState())) return;

        // The answer replaces the calls, so the list keeps its rows, and the
        // focus in them, while it refetches.
        const since = moment().subtract(72, 'hours').valueOf();
        dispatch(
            fetchQueueCallRecordsUnhandled(true, false, undefined, since, true),
        );
    };

const getCallActionNameFormatted = (actionName: string) =>
    actionName?.toLowerCase().replace(/[_ ]/g, '-');

export const onTopEventCallRecordActionAdded =
    (topPushNotice: TopEventCallRecordActionAdded) =>
    (dispatch: AppDispatch, getState: () => State) => {
        const {
            actionName,
            actor,
            callActionEntityKey,
            callRecordEntityKey,
            queueEntityKey,
            timestamp,
        } = topPushNotice.data;
        const actionNameFormatted = getCallActionNameFormatted(actionName);

        if (
            !queueEntityKey ||
            !callRecordEntityKey ||
            actionNameFormatted !== 'handle-unanswered'
        )
            return;
        // Not doing anything atm if action is for an entity that is not a queue call record,
        // or if action is not of type 'Marked as handled' ('handle-unanswered')

        const callActionDto = {
            agent: {
                key: actor,
                contact: selectContact(getState(), actor),
            },
            key: callActionEntityKey,
            name: actionNameFormatted,
            timestamp: timestamp,
        };

        dispatch({
            type: Actions.RESULT_CREATE_CALL_ACTION,
            callRecordKey: callRecordEntityKey,
            queueKey: queueEntityKey,
            data: callActionDto,
        });
    };

export const onTopEventCallRecordActionRemoved =
    (topPushNotice: TopEventCallRecordActionRemoved) =>
    (dispatch: AppDispatch) => {
        const {
            actionName,
            callActionEntityKey,
            callRecordEntityKey,
            queueEntityKey,
        } = topPushNotice.data;
        const actionNameFormatted = getCallActionNameFormatted(actionName);

        if (
            !queueEntityKey ||
            !callRecordEntityKey ||
            actionNameFormatted !== 'handle-unanswered'
        )
            return;
        // Not doing anything atm if action is for an entity that is not a queue call record,
        // or if action is not of type 'Marked as handled' ('handle-unanswered')

        dispatch({
            type: Actions.RESULT_DELETE_CALL_ACTION,
            callRecordKey: callRecordEntityKey,
            callActionKey: callActionEntityKey,
            queueKey: queueEntityKey,
        });

        if (actionNameFormatted === 'handle-unanswered') {
            dispatch(
                evaluateAddCallToUnhandledWidgetOnCallActionRemoved(
                    queueEntityKey,
                    callRecordEntityKey,
                ),
            );
        }
    };

const evaluateAddCallToUnhandledWidgetOnCallActionRemoved =
    (queueKey: QueueKey, callRecordKey: CallRecordKey) =>
    (dispatch: AppDispatch, getState: () => State) => {
        if (!selectMissedCallsWidgetIsMounted(getState())) return;
        // Widget not mounted, no need to add call to unhandled widget data

        const callIsInFavoriteQueue = selectQueueFavorites(getState())?.find(
            (favorite) => favorite?.getIn(['favoriteData', 'key']) === queueKey,
        );

        if (!callIsInFavoriteQueue) return;
        // Call not in favorite queue, no need to add call to unhandled widget data

        const callRecord = selectCallRecordFromKey(getState(), callRecordKey);
        if (
            !callRecord ||
            callRecord.has('answered') ||
            !callRecord.has('disconnected')
        )
            return;
        // Call record not found, or already answered or not disconnected -- doing nothing.

        // Call record is unhandled, add it to the unhandled widget data
        dispatch({
            type: Actions.RESULT_QUEUE_CALL_RECORDS_UNHANDLED_WIDGET,
            data: {
                records: [callRecord.toJS()],
            },
        });
    };

type TopEventCallRecordActionAdded = {
    type: 'call_record_action_added';
    data: {
        actionName: string;
        actor: ExtensionKey;
        callActionEntityKey: CallActionKey;
        callRecordEntityKey: CallRecordKey;
        queueEntityKey: QueueKey;
        timestamp: string;
    };
};

type TopEventCallRecordActionRemoved = {
    type: 'call_record_action_removed';
    data: {
        actionName: string;
        callActionEntityKey: CallActionKey;
        callRecordEntityKey: CallRecordKey;
        queueEntityKey: QueueKey;
    };
};
